import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { 
  LogOut, 
  RefreshCw, 
  CheckCircle2, 
  XCircle, 
  Clock,
  Loader2,
  Copy,
  ExternalLink,
  Send
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function OperatorDashboard() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState("");

  const getToken = () => localStorage.getItem("admin_token");

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    const token = getToken();
    try {
      const response = await axios.get(`${API}/operator/requests?token=${token}`);
      setRequests(response.data || []);
    } catch (error) {
      console.error("Error fetching requests:", error);
      if (error.response?.status === 403) {
        toast.error("Доступ запрещён");
        navigate("/admin/login");
      } else if (error.response?.status === 401) {
        toast.error("Сессия истекла");
        navigate("/admin/login");
      } else {
        toast.error("Ошибка загрузки заявок");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate("/admin/login");
      return;
    }

    // Decode token to get username
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setUsername(payload.sub || "Оператор");
    } catch {
      setUsername("Оператор");
    }

    fetchRequests();
    
    // Auto refresh every 30 seconds
    const interval = setInterval(fetchRequests, 30000);
    return () => clearInterval(interval);
  }, [navigate, fetchRequests]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_role");
    navigate("/admin/login");
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Скопировано");
  };

  const updateStatus = async (requestId, newStatus) => {
    const token = getToken();
    try {
      await axios.patch(`${API}/operator/requests/${requestId}?token=${token}`, {
        status: newStatus
      });
      toast.success(`Статус обновлён: ${newStatus === 'in_progress' ? 'В работе' : newStatus === 'completed' ? 'Выполнено' : 'Отменено'}`);
      fetchRequests();
    } catch (error) {
      console.error("Error updating status:", error);
      if (error.response?.status === 403) {
        toast.error("Доступ запрещён");
      } else {
        toast.error("Ошибка обновления статуса");
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-500 bg-green-500/10';
      case 'cancelled': return 'text-red-500 bg-red-500/10';
      case 'processing':
      case 'in_progress': return 'text-blue-500 bg-blue-500/10';
      default: return 'text-yellow-500 bg-yellow-500/10';
    }
  };

  const getStatusText = (status) => {
    const statuses = {
      pending: "Ожидание",
      awaiting_confirmation: "Ожидание подтверждения",
      deposit_confirmed: "Депозит подтверждён",
      in_progress: "В обработке",
      processing: "Обработка",
      completed: "Завершено",
      cancelled: "Отменено"
    };
    return statuses[status] || status;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="animate-spin text-primary" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-heading text-xl font-bold text-primary">
              SKINKOSH
            </Link>
            <span className="text-muted-foreground">|</span>
            <span className="text-sm text-muted-foreground">Оператор: {username}</span>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={fetchRequests}
              variant="outline"
              size="sm"
            >
              <RefreshCw size={16} className="mr-2" />
              Обновить
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="text-red-500 hover:text-red-400"
            >
              <LogOut size={16} className="mr-2" />
              Выйти
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold text-foreground mb-6">
          Мои заявки ({requests.length})
        </h1>

        {requests.length === 0 ? (
          <div className="text-center py-12 bg-card border border-border rounded-lg">
            <Clock size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Нет активных заявок</p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((request) => (
              <div
                key={request.id}
                className="bg-card border border-border rounded-lg p-4"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[300px]">
                    {/* Order number and status */}
                    <div className="flex items-center gap-3 mb-3">
                      <span className="bg-primary text-primary-foreground font-bold px-2 py-1 rounded">
                        #{request.order_number || request.id.slice(0, 8)}
                      </span>
                      <span className={`px-2 py-1 rounded text-sm font-medium ${getStatusColor(request.status)}`}>
                        {getStatusText(request.status)}
                      </span>
                    </div>

                    {/* Exchange info */}
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <div className="text-xs text-muted-foreground">Отправляют</div>
                        <div className="font-mono font-bold">
                          {request.from_amount} {request.from_currency?.toUpperCase()}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Получают</div>
                        <div className="font-mono font-bold text-primary">
                          {parseFloat(request.to_amount).toFixed(8)} {request.to_currency?.toUpperCase()}
                        </div>
                      </div>
                    </div>

                    {/* Contact info */}
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">Email:</span>
                        <span>{request.user_email}</span>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyToClipboard(request.user_email)}>
                          <Copy size={12} />
                        </Button>
                      </div>
                      {request.user_telegram && (
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">Telegram:</span>
                          <span>{request.user_telegram}</span>
                        </div>
                      )}
                    </div>

                    {/* Addresses */}
                    <div className="mt-3 space-y-2">
                      <div>
                        <div className="text-xs text-muted-foreground">Адрес депозита (куда отправляют):</div>
                        <div className="flex items-center gap-2">
                          <code className="text-xs bg-muted px-2 py-1 rounded truncate max-w-[300px]">
                            {request.deposit_address}
                          </code>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyToClipboard(request.deposit_address)}>
                            <Copy size={12} />
                          </Button>
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Кошелёк клиента (куда отправлять):</div>
                        <div className="flex items-center gap-2">
                          <code className="text-xs bg-muted px-2 py-1 rounded truncate max-w-[300px]">
                            {request.user_wallet}
                          </code>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyToClipboard(request.user_wallet)}>
                            <Copy size={12} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    {request.status !== 'completed' && request.status !== 'cancelled' && (
                      <>
                        <Button
                          onClick={() => updateStatus(request.id, 'in_progress')}
                          variant="outline"
                          size="sm"
                          className="text-blue-500 border-blue-500/50"
                        >
                          <Loader2 size={14} className="mr-1" />
                          В работу
                        </Button>
                        <Button
                          onClick={() => updateStatus(request.id, 'completed')}
                          variant="outline"
                          size="sm"
                          className="text-green-500 border-green-500/50"
                        >
                          <CheckCircle2 size={14} className="mr-1" />
                          Выполнено
                        </Button>
                        <Button
                          onClick={() => updateStatus(request.id, 'cancelled')}
                          variant="outline"
                          size="sm"
                          className="text-red-500 border-red-500/50"
                        >
                          <XCircle size={14} className="mr-1" />
                          Отменить
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Date */}
                <div className="mt-3 text-xs text-muted-foreground">
                  Создано: {new Date(request.created_at).toLocaleString('ru-RU')}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default OperatorDashboard;
